import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Sparkles, HandHeart, ShieldCheck, Microscope, CircleDot, Quote, Calendar } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { api } from "@/lib/api";

const FEATURE_ICONS = [Leaf, Sparkles, ShieldCheck, HandHeart, Microscope, CircleDot];

export default function Home() {
  const { t } = useTranslation();
  const [testimonials, setTestimonials] = useState([]);
  const features = t("home.features", { returnObjects: true });

  useEffect(() => {
    api.get("/testimonials").then((r) => setTestimonials(r.data.slice(0, 2))).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-12 pb-20 lg:pt-20 lg:pb-32 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 reveal reveal-1">
            <div className="label-eyebrow mb-6">{t("home.eyebrow")}</div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#2C2F2A]">
              {t("home.heroTitle1")}<br />
              <em className="text-[#8A9A86] not-italic font-light">{t("home.heroTitle2")}</em><br />
              {t("home.heroTitle3")}
            </h1>
            <p className="mt-8 text-lg text-[#5C6356] leading-relaxed max-w-lg font-light">
              <Trans i18nKey="home.heroIntro" components={{ b: <b className="font-medium text-[#2C2F2A]" /> }} />
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/prenotazioni" data-testid="hero-cta-book" className="btn-primary">
                <Calendar size={16} strokeWidth={1.5} />
                {t("home.ctaBook")}
              </Link>
              <Link to="/servizi" data-testid="hero-cta-services" className="btn-secondary">
                {t("home.ctaServices")} <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6 relative reveal reveal-2">
            <div className="relative">
              <div className="absolute -top-6 -left-6 w-40 h-40 rounded-full bg-[#8A9A86]/15 -z-0" />
              <div className="absolute -bottom-6 -right-6 w-56 h-56 rounded-full bg-[#C27B66]/15 -z-0" />
              <img
                src="https://customer-assets.emergentagent.com/job_olistico-care/artifacts/11gjf310_IMG_7242.jpeg"
                alt="Valeria Cantore — Studio di Massoterapia"
                className="relative w-full h-[480px] lg:h-[560px] object-cover rounded-[2rem] grain"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md border border-[#E6E2D8] rounded-2xl p-5 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-[#8A9A86] flex items-center justify-center text-white">
                  <Leaf size={18} strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-serif text-xl leading-tight">{t("home.approachTitle")}</div>
                  <div className="text-xs text-[#5C6356]">{t("home.approachSub")}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="bg-[#F3F0EA] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <div className="label-eyebrow mb-4">{t("home.featuresEyebrow")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[#2C2F2A] leading-tight">
              {t("home.featuresTitle1")} <em className="text-[#8A9A86] font-light">{t("home.featuresTitle2")}</em>.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
            {features.map((f, i) => {
              const Icon = FEATURE_ICONS[i] || Leaf;
              return (
                <div key={i} data-testid={`home-feature-${i}`} className="card-soft p-8">
                  <Icon className="w-7 h-7 text-[#8A9A86] mb-5" strokeWidth={1.5} />
                  <div className="font-serif text-2xl mb-2">{f.title}</div>
                  <p className="text-sm text-[#5C6356] leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-12 text-center">
            <Link to="/servizi" data-testid="home-services-link" className="btn-secondary">
              {t("home.featuresAll")} <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <Quote className="w-10 h-10 mx-auto text-[#8A9A86] mb-8" strokeWidth={1} />
          <p className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight text-[#2C2F2A] italic">
            "{t("home.quote")}"
          </p>
          <p className="mt-6 label-eyebrow">{t("home.quoteAuthor")}</p>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <img
            src="https://customer-assets.emergentagent.com/job_olistico-care/artifacts/vejwy24o_df2c8138-5cb9-4152-b8c1-cc4a88943a45.jpeg"
            alt="Valeria Cantore — Certificazione Thai Massage"
            className="w-full h-[520px] object-cover rounded-[2rem]"
          />
        </div>
        <div className="lg:col-span-7 lg:pl-8">
          <div className="label-eyebrow mb-4">{t("home.aboutEyebrow")}</div>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-[#2C2F2A]">
            {t("home.aboutTitle1")} <br />
            <em className="text-[#8A9A86] font-light">{t("home.aboutTitle2")}</em>
          </h2>
          <p className="mt-6 text-[#5C6356] leading-relaxed text-lg">{t("home.aboutText")}</p>
          <Link to="/chi-sono" data-testid="home-about-link" className="btn-secondary mt-8">
            {t("home.aboutMore")} <ArrowRight size={16} strokeWidth={1.5} />
          </Link>
        </div>
      </section>

      {/* TESTIMONIALS PREVIEW */}
      {testimonials.length > 0 && (
        <section className="bg-[#F3F0EA] py-20 lg:py-28">
          <div className="max-w-6xl mx-auto px-5 sm:px-8">
            <div className="text-center mb-14">
              <div className="label-eyebrow mb-4">{t("home.testimonialsEyebrow")}</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-[#2C2F2A]">{t("home.testimonialsTitle")}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {testimonials.map((tt, i) => (
                <div key={tt.id} data-testid={`home-testimonial-${i}`} className="card-soft p-8">
                  <Quote className="w-7 h-7 text-[#C27B66] mb-4" strokeWidth={1} />
                  <p className="font-serif text-xl leading-relaxed text-[#2C2F2A] italic line-clamp-6">"{tt.text}"</p>
                  <div className="mt-6 text-sm">
                    <div className="font-medium">{tt.name}</div>
                    <div className="text-[#5C6356]">{tt.role}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-12">
              <Link to="/testimonianze" data-testid="home-testimonials-link" className="btn-secondary">
                {t("home.testimonialsAll")} <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 lg:py-32">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <div className="label-eyebrow mb-4">{t("home.ctaEyebrow")}</div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2C2F2A] leading-tight">
            {t("home.ctaTitle1")}<br />
            <em className="text-[#8A9A86] font-light">{t("home.ctaTitle2")}</em>
          </h2>
          <p className="mt-6 text-lg text-[#5C6356] max-w-2xl mx-auto">{t("home.ctaText")}</p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/prenotazioni" data-testid="home-bottom-cta-book" className="btn-primary">
              <Calendar size={16} strokeWidth={1.5} /> {t("home.ctaBookNow")}
            </Link>
            <Link to="/contatti" data-testid="home-bottom-cta-contact" className="btn-secondary">
              {t("home.ctaContact")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
